import React from "react";
import { FaPrint, FaTimes, FaReceipt } from "react-icons/fa";
import receiptSaiLogo from "../assets/receipt-sai-logo.png";
import { amountToWordsIndian } from "../utils/numberToWords";
import "../styles/SathyanarayanaReceipt.css";

// Helper to map Pooja names to authentic Tamil titles
export const getPoojaTamilDetails = (poojaName = "") => {
  const lower = (poojaName || "").toLowerCase().trim();

  if (lower.includes("sathya")) {
    return {
      tamilTitle: "பௌர்ணமி சத்தியநாராயண பூஜை",
      isSathya: true,
      defaultTime: "மாலை 06:00 மணி",
    };
  }
  if (lower.includes("dhuni")) {
    return {
      tamilTitle: "வேண்டுதல் நிறைவேறும் துனி பூஜை",
      isDhuni: true,
      defaultTime: "மாலை 06:30 மணி",
    };
  }
  if (lower.includes("coconut") || lower.includes("thengai") || lower.includes("mattai")) {
    return {
      tamilTitle: "மட்டை தேங்காய் பூஜை",
      isCoconut: true,
      defaultTime: "காலை 10:00 மணி",
    };
  }
  if (lower.includes("108") || lower.includes("kalasam")) {
    return {
      tamilTitle: "108 கலசாபிஷேக பூஜை",
      defaultTime: "காலை 09:00 மணி",
    };
  }
  if (lower.includes("gomata") || lower.includes("gho") || lower.includes("cow")) {
    return {
      tamilTitle: "தினசரி கோ பூஜை / கோமாதா தரிசனம்",
      defaultTime: "காலை 07:30 மணி",
    };
  }
  if (lower.includes("santhana") || lower.includes("bhagya")) {
    return {
      tamilTitle: "சந்தான பாக்கிய பூஜை",
      defaultTime: "காலை 09:30 மணி",
    };
  }
  if (lower.includes("amavasya")) {
    return {
      tamilTitle: "அமாவாசை சிறப்பு பூஜை",
      defaultTime: "மாலை 06:00 மணி",
    };
  }
  if (lower.includes("murugan")) {
    return {
      tamilTitle: "ஸ்ரீ பாலமுருகன் பூஜை",
      defaultTime: "காலை 08:30 மணி",
    };
  }

  return {
    tamilTitle: poojaName || "ஸ்ரீ ஞானசாயிபாபா சிறப்பு பூஜை",
    defaultTime: "காலை / மாலை",
  };
};

const SathyanarayanaReceipt = ({ booking, isOpen = true, onClose }) => {
  if (!isOpen || !booking) return null;

  const poojaInfo = getPoojaTamilDetails(booking.poojaName);
  const isSathya = poojaInfo.isSathya;
  const isDhuni = poojaInfo.isDhuni;
  const isCoconut = poojaInfo.isCoconut;
  const tamilTitle = poojaInfo.tamilTitle;

  // Format Date to DD/MM/YYYY
  const formatDate = (dateString) => {
    if (!dateString) {
      const d = new Date();
      return `${String(d.getDate()).padStart(2, "0")}/${String(
        d.getMonth() + 1
      ).padStart(2, "0")}/${d.getFullYear()}`;
    }
    if (typeof dateString === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateString.trim())) {
      const [yyyy, mm, dd] = dateString.trim().split("-");
      return `${dd}/${mm}/${yyyy}`;
    }
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return String(dateString);
    return `${String(d.getDate()).padStart(2, "0")}/${String(
      d.getMonth() + 1
    ).padStart(2, "0")}/${d.getFullYear()}`;
  };

  const devoteeName =
    booking.devoteeName || booking.name || booking.donorName || "";
  const email = booking.email || "";
  const address = booking.address || "";
  const amountNumber = Number(booking.amount || 1000);
  const amountWordsEn = amountToWordsIndian(amountNumber);
  const amountWordsTa = amountNumber === 1000 ? "ஆயிரம் ரூபாய் மட்டும்" : "";
  const amountDisplayWords = amountWordsTa
    ? `${amountWordsTa} (${amountWordsEn})`
    : amountWordsEn;

  // Receipt Number: use clean numeric token or bookingId
  const receiptNum =
    booking.receiptNumber ||
    (booking.bookingId
      ? String(booking.bookingId).replace(/\D/g, "").slice(-4) || "2300"
      : "2300");

  const todayDate = formatDate(booking.createdAt || new Date());
  const poojaDate = formatDate(booking.poojaDate) || todayDate;
  const poojaTime = booking.poojaTime || poojaInfo.defaultTime;

  const handlePrint = () => {
    const printArea = document.getElementById("sathya-receipt-print-area");
    if (!printArea) {
      window.print();
      return;
    }

    const oldFrame = document.getElementById("receipt-print-iframe");
    if (oldFrame) {
      oldFrame.remove();
    }

    const printFrame = document.createElement("iframe");
    printFrame.id = "receipt-print-iframe";
    printFrame.style.position = "fixed";
    printFrame.style.top = "0";
    printFrame.style.left = "0";
    printFrame.style.width = "100vw";
    printFrame.style.height = "100vh";
    printFrame.style.border = "none";
    printFrame.style.opacity = "0.01";
    printFrame.style.pointerEvents = "none";
    printFrame.style.zIndex = "-99999";
    document.body.appendChild(printFrame);

    const doc = printFrame.contentDocument || printFrame.contentWindow.document;

    // Collect all stylesheets from current document
    let stylesHtml = "";
    document.querySelectorAll('style, link[rel="stylesheet"]').forEach((node) => {
      stylesHtml += node.outerHTML;
    });

    doc.open();
    doc.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <base href="${window.location.origin}/" />
          <title>${tamilTitle} - ஸ்ரீ ஞானசாயிபாபா மந்திர்</title>
          <meta charset="utf-8" />
          ${stylesHtml}
          <style>
            @page {
              size: A4 portrait;
              margin: 6mm 10mm;
            }
            *, *::before, *::after {
              box-sizing: border-box;
              visibility: visible !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            html, body {
              background: #ffffff !important;
              color: #103260 !important;
              margin: 0 !important;
              padding: 0 !important;
              width: 100% !important;
              height: auto !important;
              overflow: visible !important;
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Mukta Malar", sans-serif;
            }
            body, body * {
              visibility: visible !important;
            }
            .sathya-receipt-slip,
            #sathya-receipt-print-area,
            #sathya-receipt-print-area * {
              visibility: visible !important;
            }
            .sathya-receipt-slip {
              width: 100% !important;
              max-width: 680px !important;
              box-shadow: none !important;
              border: 2.5px solid #103260 !important;
              margin: 0 auto !important;
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }
            .trust-name {
              margin-bottom: 4px !important;
              line-height: 1.3 !important;
            }
            .trust-designation {
              line-height: 1.3 !important;
            }
            @media print {
              *, *::before, *::after {
                visibility: visible !important;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
              body, body * {
                visibility: visible !important;
              }
              .sathya-receipt-slip,
              #sathya-receipt-print-area,
              #sathya-receipt-print-area * {
                visibility: visible !important;
              }
            }
          </style>
        </head>
        <body>
          <div style="width: 100%; display: flex; justify-content: center; padding: 10px 0;">
            ${printArea.outerHTML}
          </div>
        </body>
      </html>
    `);
    doc.close();

    const triggerPrint = () => {
      try {
        printFrame.contentWindow.focus();
        printFrame.contentWindow.print();
      } catch (err) {
        console.error("Print error:", err);
      }
    };

    // Ensure images and fonts are loaded before opening print dialog
    setTimeout(() => {
      const imgs = doc.images;
      if (!imgs || imgs.length === 0) {
        triggerPrint();
      } else {
        let loaded = 0;
        const total = imgs.length;
        let fired = false;
        const checkDone = () => {
          if (!fired) {
            fired = true;
            setTimeout(triggerPrint, 150);
          }
        };

        const handleImageEvent = () => {
          loaded++;
          if (loaded >= total) checkDone();
        };

        for (let i = 0; i < total; i++) {
          if (imgs[i].complete && imgs[i].naturalWidth !== 0) {
            loaded++;
          } else {
            imgs[i].onload = handleImageEvent;
            imgs[i].onerror = handleImageEvent;
          }
        }
        if (loaded >= total) {
          checkDone();
        } else {
          setTimeout(checkDone, 450);
        }
      }
    }, 150);
  };

  return (
    <div className="sathya-receipt-backdrop">
      <div
        className="sathya-receipt-modal"
        role="dialog"
        aria-modal="true"
      >
        {/* ACTION BAR (Hidden in print) */}
        <div className="sathya-receipt-actions">
          <div className="actions-title">
            <FaReceipt /> ஸ்ரீ ஞானசாயிபாபா மந்திர் • {tamilTitle} ரசீது
          </div>

          <div className="actions-buttons">
            <button
              type="button"
              className="receipt-action-btn btn-print"
              onClick={handlePrint}
              title="Print Receipt or Save as PDF"
            >
              <FaPrint /> Print / Save PDF
            </button>
            {onClose && (
              <button
                type="button"
                className="receipt-action-btn btn-close"
                onClick={onClose}
                title="Close receipt preview"
              >
                <FaTimes /> Close
              </button>
            )}
          </div>
        </div>

        {/* PRINTABLE RECEIPT CONTAINER */}
        <div className="sathya-receipt-scroll-wrap">
          <div className="sathya-receipt-slip" id="sathya-receipt-print-area">
            {/* INNER DOUBLE BORDER FRAME */}
            <div className="sathya-receipt-inner-frame">
              {/* TOP HEADER WORDS */}
              <div className="sathya-top-words-row">
                <span className="sathya-word-left">நம்பிக்கை!</span>
                <span className="sathya-word-center">உ</span>
                <span className="sathya-word-right">பொறுமை!</span>
              </div>

              {/* MANDIR LOGO & TITLE HEADER */}
              <div className="sathya-header-banner">
                <div className="sathya-logo-frame">
                  <img
                    src={receiptSaiLogo}
                    alt="Sri Gnana Sai Baba"
                    className="sathya-baba-sketch"
                  />
                </div>

                <div className="sathya-title-block">
                  <h1 className="sathya-mandir-title">
                    ஸ்ரீ ஞானசாயிபாபா மந்திர்
                  </h1>
                  <p className="sathya-mandir-sub">
                    வெங்கடேஸ்வரா நகர், அம்பத்தூர், சென்னை-53.
                  </p>
                  <p className="sathya-mandir-phone">
                    செல் : <strong>98412 45070</strong>, <strong>63817 20140</strong>
                  </p>
                </div>
              </div>

              {/* RECEIPT NO & DATE ROW */}
              <div className="sathya-meta-row">
                <div className="sathya-sno-field">
                  <span className="meta-label">எண் :</span>
                  <span className="meta-value">{receiptNum}</span>
                </div>

                <div className="sathya-date-field">
                  <span className="meta-label">தேதி :</span>
                  <span className="meta-value-line">{todayDate}</span>
                </div>
              </div>

              {/* MAIN DEVOTEE DETAILS SECTION */}
              <div className="sathya-body-fields">
                {/* NAME */}
                <div className="sathya-field-line">
                  <span className="field-lbl">பெயர் :</span>
                  <span className="field-dotted-fill">{devoteeName || "\u00A0"}</span>
                </div>

                {/* EMAIL */}
                <div className="sathya-field-line">
                  <span className="field-lbl">மின்னஞ்சல் :</span>
                  <span className="field-dotted-fill">{email || "\u00A0"}</span>
                </div>

                {/* ADDRESS */}
                <div className="sathya-field-line">
                  <span className="field-lbl">விலாசம் :</span>
                  <span className="field-dotted-fill">{address || "\u00A0"}</span>
                </div>

                {/* AMOUNT IN WORDS */}
                <div className="sathya-field-line">
                  <span className="field-lbl">தொகை :</span>
                  <span className="field-dotted-fill">{amountDisplayWords || "\u00A0"}</span>
                </div>

                {/* ================= POOJA SPECIFIC DETAILS ================= */}
                {isSathya ? (
                  /* SATHYANARAYANA POOJA ROW (PHOTO 1) */
                  <>
                    <div className="sathya-pooja-highlight-row">
                      <span className="pooja-name-text">
                        பௌர்ணமி சத்தியநாராயண பூஜை :
                      </span>
                      <span className="pooja-amount-badge">
                        Rs.{amountNumber}/-
                      </span>
                    </div>

                    <div className="sathya-date-time-row">
                      <div className="date-time-col">
                        <span className="field-lbl">நாள் :</span>
                        <span className="field-dotted-fill">{poojaDate || "\u00A0"}</span>
                      </div>

                      <div className="date-time-col">
                        <span className="field-lbl">நேரம் :</span>
                        <span className="field-dotted-fill">{poojaTime || "\u00A0"}</span>
                      </div>
                    </div>
                  </>
                ) : (
                  /* ALL REMAINING POOJAS (PHOTO 2) */
                  <div className="general-poojas-block">
                    {isDhuni && (
                      <>
                        <div className="sathya-pooja-highlight-row">
                          <span className="pooja-name-text">
                            வேண்டுதல் நிறைவேறும் துனி பூஜை :
                          </span>
                          <span className="pooja-amount-badge">
                            Rs.{amountNumber}/-
                          </span>
                        </div>
                        <div className="sathya-pooja-highlight-row unselected-row">
                          <span className="pooja-name-text">
                            மட்டை தேங்காய் பூஜை :
                          </span>
                          <span className="pooja-amount-badge empty-badge">
                            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                          </span>
                        </div>
                      </>
                    )}

                    {isCoconut && (
                      <>
                        <div className="sathya-pooja-highlight-row unselected-row">
                          <span className="pooja-name-text">
                            வேண்டுதல் நிறைவேறும் துனி பூஜை :
                          </span>
                          <span className="pooja-amount-badge empty-badge">
                            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                          </span>
                        </div>
                        <div className="sathya-pooja-highlight-row">
                          <span className="pooja-name-text">
                            மட்டை தேங்காய் பூஜை :
                          </span>
                          <span className="pooja-amount-badge">
                            Rs.{amountNumber}/-
                          </span>
                        </div>
                      </>
                    )}

                    {!isDhuni && !isCoconut && (
                      <div className="sathya-pooja-highlight-row">
                        <span className="pooja-name-text">
                          {tamilTitle}{" "}
                          {booking.poojaName &&
                            !tamilTitle.includes(booking.poojaName) &&
                            booking.poojaName !== tamilTitle &&
                            `(${booking.poojaName})`}{" "}
                          :
                        </span>
                        <span className="pooja-amount-badge">
                          Rs.{amountNumber}/-
                        </span>
                      </div>
                    )}

                    {/* DATE & TIME (Stacked vertically on the left as in 2nd photo) */}
                    <div className="general-date-time-stack">
                      <div className="sathya-field-line general-subline">
                        <span className="field-lbl">நாள் :</span>
                        <span className="field-dotted-fill">{poojaDate || "\u00A0"}</span>
                      </div>
                      <div className="sathya-field-line general-subline">
                        <span className="field-lbl">நேரம் :</span>
                        <span className="field-dotted-fill">{poojaTime || "\u00A0"}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* ================= BOTTOM SECTION ================= */}
              {isSathya ? (
                /* SATHYANARAYANA POOJA: ITEMS TO BRING BOX + TRUST SIGNOFF */
                <div className="sathya-bottom-row">
                  {/* LEFT: ITEMS TO BRING TABLE */}
                  <div className="sathya-items-box">
                    <div className="items-box-header">
                      சத்தியநாராயண பூஜைக்கு எடுத்து
                      <br />
                      வரவேண்டிய பொருட்கள்
                    </div>

                    <ul className="items-list">
                      <li>
                        <span className="item-name">குத்துவிளக்கு</span>
                        <span className="item-qty">- 1</span>
                      </li>
                      <li>
                        <span className="item-name">அகல்விளக்கு</span>
                        <span className="item-qty">- 1</span>
                      </li>
                      <li>
                        <span className="item-name">தாம்பூலம்</span>
                        <span className="item-qty">- 1</span>
                      </li>
                      <li>
                        <span className="item-name">பஞ்சபாத்திரம் உத்தரணி</span>
                        <span className="item-qty">- 1</span>
                      </li>
                      <li>
                        <span className="item-name">சிறிய பாபா விக்கிரகம்</span>
                        <span className="item-qty">- 1</span>
                      </li>
                    </ul>
                  </div>

                  {/* RIGHT: TRUST SIGNOFF */}
                  <div className="sathya-trust-signoff">
                    <div className="trust-name">
                      ஸ்ரீ ஞானசாயிபாபா மந்திர் சாரிடபிள் டிரஸ்ட்
                    </div>
                    <div className="trust-designation">நிறுவனர்</div>
                  </div>
                </div>
              ) : (
                /* ALL REMAINING POOJAS: TRUST SIGNOFF (PHOTO 2) */
                <div className="sathya-bottom-row general-pooja-bottom">
                  <div className="sathya-trust-signoff">
                    <div className="trust-name">
                      ஸ்ரீ ஞானசாயிபாபா மந்திர் சாரிடபிள் டிரஸ்ட்
                    </div>
                    <div className="trust-designation">நிறுவனர்</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SathyanarayanaReceipt;
